import { RateInterval } from '__types__/enums';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RateDescriptionCopy } from 'v5/core/rates/describeRate';
import type { RateFee } from 'v5/core/rates/resourceRate';

/**
 * Words the rate field puts on screen, in the current language. Kept apart
 * from the components so `describeRate` stays a plain function.
 */
export type RateCopy = RateDescriptionCopy & {
  /** "Per day" — in the billing unit list. */
  unitOption(interval: RateInterval): string;
  /** "Day rate" — name a new rate gets until someone types one. */
  autoName(interval: RateInterval): string;
  /** "Agency" — visible label. */
  feeShort(fee: RateFee): string;
  /** "Agency fee" — accessible name and error messages. */
  feeFull(fee: RateFee): string;
};

export function useRateCopy(): RateCopy {
  const { t, i18n } = useTranslation('core');
  const locale = i18n.language;
  return useMemo<RateCopy>(
    () => ({
      locale,
      unitShort: interval => t(`rateEditor.unit.${interval}`),
      unitSpoken: interval => t(`rateEditor.unitSpoken.${interval}`),
      unitOption: interval => t(`rateEditor.unitOption.${interval}`),
      autoName: interval => t(`rateEditor.autoName.${interval}`),
      feeShort: fee => t(`rateEditor.fee.${fee.key}`),
      feeFull: fee => t(`rateEditor.feeFull.${fee.key}`),
      feeSummary: (percent, fee) =>
        t('rateEditor.feeSummary', {
          percent,
          fee: t(`rateEditor.fee.${fee.key}`).toLocaleLowerCase(locale)
        }),
      feesSpoken: fees =>
        t('rateEditor.plusFees', {
          fees: listFormat(
            locale,
            fees.map(({ percent, fee }) =>
              t('rateEditor.feeSpoken', {
                percent,
                fee: t(`rateEditor.feeFull.${fee.key}`).toLocaleLowerCase(
                  locale
                )
              })
            )
          )
        }),
      noAmount: t('rateEditor.noAmount'),
      unnamed: t('rateEditor.unnamed')
    }),
    [t, locale]
  );
}

function listFormat(locale: string, items: string[]): string {
  try {
    return new Intl.ListFormat(locale, {
      style: 'long',
      type: 'conjunction'
    }).format(items);
  } catch {
    return items.join(', ');
  }
}
